import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { PROMETHEAN_NEUTRAL_WHITE } from '../../../styles';
import classnames from 'classnames';
export var TABLE_SCROLLER_CLASS = 'embers-table-scroller';
export var TableHeadScrollHandler = function TableHeadScrollHandler(_ref) {
  var id = _ref.id,
    tableHeadElem = _ref.tableHeadElem,
    renderHeader = _ref.renderHeader,
    needsRerender = _ref.needsRerender,
    onTableScrolled = _ref.onTableScrolled,
    scrollContainerClass = _ref.scrollContainerClass,
    children = _ref.children;
  // tableScrollerRef is the element which is the scroll container for the table
  //  **NOTE** that it must have the scrollContainerClass in its classlist
  var tableScrollerRef = useRef();
  // cloneElemRef is not used - it is just to prevent overwriting the original ref
  var cloneElemRef = useRef(null);

  // we use the state var clonedHeader for triggering render, and the
  //  member var clonedHeaderRef is so we know the state between renders e.g. onScroll
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    clonedHeader = _useState2[0],
    setClonedHeader = _useState2[1];
  var clonedHeaderRef = useRef(null);

  // needsRecloning mechanism is so that we only call renderHeader when in a render,
  //  because if we call it directly as a callback from the scroll-handler
  //  React gets confused and calls it with old props
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    needsRecloning = _useState4[0],
    setNeedsRecloning = _useState4[1];
  useEffect(function () {
    // this is for if we have a cloned header present and the container
    //  does something that requires re-rendering the clone, e.g. removing a column
    if (clonedHeaderRef.current) {
      setNeedsRecloning(needsRerender);
    }
  }, [needsRerender]);

  // when we change the cloned header we need to change it two places so call updateClonedHeader
  var updateClonedHeader = function updateClonedHeader(newClone) {
    clonedHeaderRef.current = newClone;
    setClonedHeader(newClone);
  };
  var makeClonedHeader = function makeClonedHeader() {
    var _tableHeadElem$parent;
    if (!tableHeadElem || !tableScrollerRef.current) {
      return;
    }

    // find scroller offset to window
    var anElem = tableScrollerRef.current;
    var scrollerOffsetToWindowTop = anElem.offsetTop;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      var offsetFromScrollerElem = anElem.offsetParent;
      if (!offsetFromScrollerElem) {
        break;
      }
      scrollerOffsetToWindowTop += offsetFromScrollerElem.offsetTop;
      anElem = offsetFromScrollerElem;
    }

    // get the width of the element cells so the cloned header matches them
    var headerElemChildren = tableHeadElem.children[0].children;
    var elemWidths = [];
    for (var i = 0; i < headerElemChildren.length; i++) {
      var cellElem = headerElemChildren[i];
      if (!cellElem.classList.contains('fixed-checkbox')) {
        elemWidths.push(cellElem.offsetWidth);
      }
    }
    var clone = renderHeader(cloneElemRef, classnames('EmbersTableHead-root', 'EmbersTableHead-fixed'), {
      position: 'fixed',
      zIndex: '10',
      backgroundColor: PROMETHEAN_NEUTRAL_WHITE,
      top: scrollerOffsetToWindowTop,
      width: (_tableHeadElem$parent = tableHeadElem.parentElement) === null || _tableHeadElem$parent === void 0 || (_tableHeadElem$parent = _tableHeadElem$parent.parentElement) === null || _tableHeadElem$parent === void 0 ? void 0 : _tableHeadElem$parent.offsetWidth
    }, elemWidths, id);
    updateClonedHeader(clone);
  };
  function handleScroll() {
    if (onTableScrolled) {
      onTableScrolled();
    }
    if (!tableHeadElem || !tableScrollerRef.current) {
      return;
    }
    // find header container position relative to top of scroller
    var anElem = tableHeadElem.parentElement;
    if (!anElem) {
      return;
    }
    var elemOffsetFromScrollerTop = anElem.offsetTop;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      var offsetElem = anElem.offsetParent;
      if (!offsetElem) {
        // if the header is not connected all the way out to root elem
        //  then we get out of here and set cloned header to null
        break;
      }
      if (offsetElem === tableScrollerRef.current) {
        if (tableScrollerRef.current.scrollTop >= elemOffsetFromScrollerTop) {
          if (!clonedHeaderRef.current) {
            setNeedsRecloning(true);
          }
          return;
        }
      }
      elemOffsetFromScrollerTop += offsetElem.offsetTop;
      anElem = offsetElem;
    }
    updateClonedHeader(null);
  }
  var getTableScrollerElem = function getTableScrollerElem() {
    var anElem = tableHeadElem;
    // in cases where we don't want the special table-scrolling behavior,
    //  define scrollContainerClass as an empty string
    if (scrollContainerClass === '') {
      return null;
    }
    var scrollerClass = scrollContainerClass === undefined ? TABLE_SCROLLER_CLASS : scrollContainerClass;
    if (anElem) {
      while (!anElem.classList.contains(scrollerClass)) {
        var _anElem;
        var nextParent = (_anElem = anElem) === null || _anElem === void 0 ? void 0 : _anElem.parentElement;
        if (!nextParent) {
          return null;
        }
        anElem = nextParent;
      }
    }
    return anElem;
  };
  useEffect(function () {
    var tableScrollerElem = getTableScrollerElem();
    if (tableScrollerElem) {
      tableScrollerRef.current = tableScrollerElem;
      tableScrollerElem.addEventListener('scroll', handleScroll);
    }
    return function () {
      if (tableScrollerRef.current) {
        tableScrollerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [tableHeadElem]);
  useEffect(function () {
    if (needsRecloning) {
      setNeedsRecloning(false);
      setClonedHeader(null);
      makeClonedHeader();
    }
  }, [needsRecloning]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children, clonedHeader);
};